import { useEffect, useMemo, useState } from "react";
import useApiSource from "../../components/terminal/useApiSource.js";
import { terminalAPI } from "../../apis/TerminalAPI.jsx";
import { vendorsAPI } from "../../apis/VendorsAPI.jsx";
import { portfolioAPI } from "../../apis/PortfolioAPI.jsx";
import { flattenTxns } from "../home/TreasurySummary.jsx";
import { MOCK_VENDORS, MOCK_PEOPLE, MOCK_BILLS, MOCK_QUEUE } from "./mockData.js";

/* Adapter hooks for the Pay tab.
   - useVendors  → legacy /vendors/my_vendor_accounts (proven endpoint
                   the legacy Vendors page already uses). The spec
                   §B.1 (/vendors?include_due=true) is deployed but
                   currently 403s for our role; switch back to §B.1
                   here once the BE team opens it up.
   - useBills    → §B.2   /bills (still spec-only, falls back to mock)
   - usePayQueue → derived locally; back team has no equivalent yet
   - usePeople   → no spec endpoint; legacy PayMeAPI has contacts but
                   shape diverges. Keep as mock for now. */

/* Bank-name lookup for common US routings. Covers the top issuers we
   see in production data; falls back to "US Bank" so the slot is
   never empty. When the BE adds a bank_name field, replace this with
   the server value. */
const BANK_BY_ROUTING = {
  "021000021": "JPMorgan Chase",
  "021000089": "Citibank",
  "021200339": "Bank of America",
  "026009593": "Bank of America",
  "121000358": "Bank of America",
  "054001204": "Bank of America",
  "031176110": "Capital One",
  "065000090": "Hancock Whitney",
  "121000248": "Wells Fargo",
  "122000661": "Wells Fargo",
  "053000196": "Wells Fargo",
  "031100209": "TD Bank",
  "221271935": "TD Bank",
  "011500120": "Citizens Bank",
  "123000220": "U.S. Bank",
  "081000032": "U.S. Bank",
  "011000028": "State Street",
  "072403473": "Comerica",
  "124303120": "JPMorgan Chase",
  "021502011": "Banco Popular",
};

function bankFromRouting(routing) {
  const r = String(routing || "").trim();
  if (BANK_BY_ROUTING[r]) return BANK_BY_ROUTING[r];
  if (/^\d{9}$/.test(r))  return "US Bank";
  return r ? `Routing ${r}` : "—";
}

/* pay_method_type → RailTag key. Values seen in live data:
   "rtp", "RTP", "bank_wire_us", "ach", "sepa", "swift", etc. */
function railFromPayMethod(type) {
  const v = String(type || "").toLowerCase();
  if (v.includes("rtp") || v.includes("fednow"))       return "FedNow";
  if (v.includes("ach"))                               return "ACH";
  if (v.includes("wire") && v.includes("us"))          return "Wire";
  if (v.includes("swift") || v.includes("wire") || v.includes("international")) return "SWIFT";
  if (v.includes("sepa") && v.includes("instant"))     return "SEPA-I";
  if (v.includes("sepa"))                              return "SEPA";
  if (v.includes("fps") || v.includes("faster"))       return "FPS";
  return "Wire";
}

function titleCase(s) {
  return String(s || "")
    .toLowerCase()
    .split(/\s+/)
    .filter(Boolean)
    .map((w) => w.charAt(0).toUpperCase() + w.slice(1))
    .join(" ");
}

/* Adapt one vendor row from /vendors/my_vendor_accounts into the
   MOCK_VENDORS contract the Pay panel renders. */
function mapVendor(v) {
  const accounts = v?.json_build_object?.accounts || [];
  const saved_methods = accounts.map((a) => {
    const meta    = a.metadata || {};
    const acctNum = String(meta.account_number || "");
    const last4   = acctNum ? acctNum.slice(-4) : "";
    const bank    = bankFromRouting(meta.routing_number);
    const masked  = last4 ? `·••• ${last4}` : "—";
    const rail    = railFromPayMethod(a.pay_method_type);
    return {
      rail,
      bank,
      account_masked: masked,
      detail_label:   `${bank} ${masked}`,
      last_used_iso:  "",      // BE doesn't expose this yet
      _payment_account_id: a.payment_account_id,
      _pay_method_type:    a.pay_method_type,
    };
  });

  /* Best-effort ISO. The legacy endpoint doesn't return a country
     code; infer from rail family. SWIFT could be any country, so
     fall back to blank for international wires. */
  const firstRail = saved_methods[0]?.rail || "";
  const iso = (firstRail === "FedNow" || firstRail === "ACH" || firstRail === "Wire") ? "US"
           :  (firstRail === "SEPA" || firstRail === "SEPA-I") ? "EU"
           :  (firstRail === "FPS") ? "GB"
           :  "";

  return {
    iso,
    name:           titleCase(v.vendor_name || ""),
    channel_id:     v.client_vendor_id || "",
    saved_methods,
    due_amount:     "0",     // BE doesn't surface AR-side due on this endpoint
    due_currency:   "USD",
    payment_terms:  "NET-30",
  };
}

export function useVendors(token) {
  const [vendors, setVendors] = useState(MOCK_VENDORS);
  const [status, setStatus]   = useState(token ? "loading" : "fallback");

  useEffect(() => {
    if (!token) { setStatus("fallback"); setVendors(MOCK_VENDORS); return; }
    let cancelled = false;
    let intervalId = null;

    const fetchOnce = async () => {
      try {
        const r = await vendorsAPI.getVendorPaymentAccounts(token);
        if (cancelled) return;
        const list = Array.isArray(r?.data) ? r.data : [];
        if (list.length === 0) {
          setStatus("live");
          setVendors([]);    // empty real list, not a fallback
          return;
        }
        const mapped = list.map(mapVendor)
          .sort((a, b) => a.name.localeCompare(b.name));
        setVendors(mapped);
        setStatus("live");
      } catch (e) {
        if (cancelled) return;
        const code = e?.response?.status;
        setStatus(code === 404 ? "feature_off" : "degraded");
        setVendors(MOCK_VENDORS);
      }
    };

    fetchOnce();
    intervalId = setInterval(fetchOnce, 60_000);
    return () => { cancelled = true; if (intervalId) clearInterval(intervalId); };
  }, [token]);

  return { vendors, status };
}

export function useBills(token) {
  const r = useApiSource({
    token,
    fetcher: terminalAPI.listBills.bind(terminalAPI),
    fallback: null,
    intervalMs: 60_000,
  });
  const bills = useMemo(() => {
    const data = r.data;
    const list = Array.isArray(data) ? data
              : Array.isArray(data?.items)   ? data.items
              : Array.isArray(data?.bills)   ? data.bills
              : Array.isArray(data?.content) ? data.content
              : null;
    return (list && list.length) ? list : MOCK_BILLS;
  }, [r.data]);
  return { bills, status: r.status };
}

/* Pay queue — composed client-side from vendor / bill / p2p
   selections. Backend doesn't own a "queue" surface in §B; this
   wrapper just hands back MOCK_QUEUE for now and is replaced when
   the user selects items in the UI. */
export function usePayQueue() {
  return { queue: MOCK_QUEUE, status: "fallback" };
}

/* People — V2 §B.5 isn't deployed yet (the spec /people endpoint
   would return a curated employees + contractors list with outstanding
   amounts). Until then, derive a "peers you've recently paid" list
   from the user's own transaction history. Groups SENT_TO_PEER and
   RECEIVED_FROM_PEER txns by peer name/handle, sorts by most-recent
   activity. Falls back to MOCK_PEOPLE when the derived list is empty
   so the pane never renders blank for new accounts. */
export function usePeople(token) {
  const [people, setPeople] = useState(MOCK_PEOPLE);
  const [status, setStatus] = useState(token ? "loading" : "fallback");

  useEffect(() => {
    if (!token) { setStatus("fallback"); setPeople(MOCK_PEOPLE); return; }
    let cancelled = false;
    let intervalId = null;

    const tick = async () => {
      try {
        const r = await portfolioAPI.getTransactionHistoryPaginated(token, 0, 100);
        if (cancelled) return;
        const flat = flattenTxns(r?.data);
        const peers = flat.filter((t) => {
          const st = String(t.transaction_subtype || "").toUpperCase();
          return st === "SENT_TO_PEER" || st === "RECEIVED_FROM_PEER";
        });
        if (peers.length === 0) {
          setStatus("live");
          setPeople(MOCK_PEOPLE);    // backend OK, just nothing to derive
          return;
        }
        const byHandle = {};
        peers.forEach((t) => {
          const handle = t.peer_handle || t.peer_email || t.peer_name || "";
          const name   = t.peer_name || t.beneficiary_name || handle || "";
          if (!name) return;
          const key = String(handle || name).toLowerCase();
          const ts  = new Date(t.creation_time || t.created_at || 0).getTime();
          if (!byHandle[key]) {
            byHandle[key] = {
              iso:           "",
              name,
              handle:        handle.startsWith("@") ? handle : (handle ? `@${handle}` : ""),
              role:          "Peer",
              saved_methods: [],
              outstanding:   "0",
              currency:      t.base_currency || "USD",
              _last_ts:      ts,
            };
          } else if (Number.isFinite(ts) && ts > byHandle[key]._last_ts) {
            byHandle[key]._last_ts = ts;
          }
        });
        const sorted = Object.values(byHandle)
          .sort((a, b) => (b._last_ts || 0) - (a._last_ts || 0))
          .slice(0, 12)
          .map(({ _last_ts, ...rest }) => rest);
        setPeople(sorted.length > 0 ? sorted : MOCK_PEOPLE);
        setStatus("live");
      } catch (e) {
        if (cancelled) return;
        const code = e?.response?.status;
        setStatus(code === 404 ? "feature_off" : "degraded");
        setPeople(MOCK_PEOPLE);
      }
    };

    tick();
    intervalId = setInterval(tick, 60_000);
    return () => { cancelled = true; if (intervalId) clearInterval(intervalId); };
  }, [token]);

  return { people, status };
}
